import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Spinner, Surface, PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Kpi, FilterBar, FunnelView, fmtDKK, fmtDate, fmtNum, PLAN_LABELS } from "./parts";

export default function AdminOverview() {
  const [filters, setFilters] = useState({ days: 30, plan: "", show_test: false });
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ days: filters.days, plan: filters.plan, show_test: filters.show_test });
    api.get(`/admin/overview?${p}`).then(({ data }) => { setData(data); setErr(null); })
      .catch((e) => setErr(apiError(e))).finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { api.get("/admin/leads").then(({ data }) => setLeads(data.leads || [])).catch(() => {}); }, []);

  const k = data?.kpis || {};
  const recentLeads = leads.slice(0, 5);
  const newLeadCount = data?.kpis?.new_leads?.value ?? 0;
  const newStatus = data?.kpis?.new_leads?.new_status ?? 0;

  return (
    <div>
      <PageHeader overline="Administrator" title="Overblik"
        subtitle="Se hvordan besøgende bliver til brugere og betalende kunder." />
      <FilterBar filters={filters} setFilters={setFilters} />

      {err ? <div className="py-16 text-center text-muted-foreground">{err}</div>
      : loading || !data ? <Spinner label="Henter overblik..." /> : (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
          <Kpi label="Besøgende" value={k.visitors?.value} change={k.visitors?.change} tip="Unikke anonymous_id. Kun i GA4 — ikke gemt serverside." />
          <Kpi label="Nye konti" value={k.new_accounts?.value} change={k.new_accounts?.change} tip="Unikke brugere oprettet (sign_up) i perioden. Admin/test ekskluderet." />
          <Kpi label="Analyser" value={k.analyses?.value} change={k.analyses?.change} tip="Gennemførte virksomhedsanalyser (onboarding fuldført)." />
          <Kpi label="Prøveperioder" value={k.trials?.value} change={k.trials?.change} tip="Unikke Stripe-abonnementer med trial_started i perioden." />
          <Kpi label="Nye betalende" value={k.new_paying?.value} change={k.new_paying?.change} tip="Første positive betaling pr. virksomhed i perioden." />
          <Kpi label="MRR ekskl. moms" value={k.mrr_excl_vat?.value} isMoney tip="Månedlig abonnementsomsætning ekskl. moms fra aktive betalende abonnementer (Stripe). Free indgår ikke." />
          <Kpi label="Nye henvendelser" value={k.new_leads?.value} change={k.new_leads?.change} tip="Henvendelser fra Book møde oprettet i perioden." />
        </div>

        <Surface className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-medium">Primær funnel</h2>
            <span className="text-xs text-muted-foreground">Serverside-trin er reelle; GA4-kun-trin vises som —</span>
          </div>
          <FunnelView steps={data.funnel} />
        </Surface>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <Surface className="lg:col-span-2 min-w-0">
            <h2 className="font-display font-medium mb-4">Planfordeling</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 font-medium">Plan</th><th className="py-2 font-medium text-right">Virksomheder</th>
                  <th className="py-2 font-medium text-right">Prøveperioder</th><th className="py-2 font-medium text-right">Betalende</th>
                  <th className="py-2 font-medium text-right">MRR ekskl. moms</th>
                </tr></thead>
                <tbody>
                  {data.plan_distribution.map((p) => (
                    <tr key={p.plan} className="border-b border-border/50" data-testid={`plan-row-${p.plan}`}>
                      <td className="py-2.5"><Badge variant="secondary">{p.label}</Badge></td>
                      <td className="py-2.5 text-right font-mono">{fmtNum(p.active_companies)}</td>
                      <td className="py-2.5 text-right font-mono">{fmtNum(p.trials)}</td>
                      <td className="py-2.5 text-right font-mono">{fmtNum(p.paying)}</td>
                      <td className="py-2.5 text-right font-mono">{fmtDKK(p.mrr_excl_vat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Surface>

          <Surface>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-medium">Seneste henvendelser</h2>
              <Link to="/admin/henvendelser" className="text-xs text-muted-foreground hover:text-foreground">Se alle</Link>
            </div>
            <div className="flex gap-2 mb-3 text-xs">
              <span className="text-muted-foreground">{fmtNum(newLeadCount)} nye i perioden</span>
              <span className="text-muted-foreground">· {fmtNum(newStatus)} med status Ny</span>
            </div>
            {recentLeads.length === 0 ? <p className="text-sm text-muted-foreground">Ingen henvendelser endnu.</p> : (
              <ul className="space-y-2">
                {recentLeads.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 border-b border-border/50 pb-2 last:border-0">
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{l.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{l.source_page}</p></div>
                    <span className="text-xs text-muted-foreground shrink-0">{fmtDate(l.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        </div>

        <Surface>
          <h2 className="font-display font-medium mb-4">Udvikling over tid</h2>
          {data.timeseries.length === 0 ? <p className="text-sm text-muted-foreground">Ingen data i perioden.</p> : (
            <MiniChart series={data.timeseries} />
          )}
        </Surface>
      </>)}
    </div>
  );
}

function MiniChart({ series }) {
  const keys = [["accounts", "Nye konti", "#1dddbf"], ["trials", "Prøveperioder", "#f5a623"], ["payments", "Betalende", "#34d399"]];
  const W = 1000, H = 240, PAD = 8;
  const n = series.length;
  const max = Math.max(...series.flatMap((d) => keys.map(([k]) => d[k])), 1) * 1.18;
  const fmt = (iso) => { const p = iso.split("-"); return `${+p[2]}/${+p[1]}`; };
  const step = Math.max(1, Math.ceil(n / 8));
  const xOf = (i) => (n <= 1 ? W / 2 : (i / (n - 1)) * (W - 2 * PAD) + PAD);
  const yOf = (v) => H - (v / max) * (H - 12) - 6;

  const smooth = (pts) => {
    if (pts.length < 2) return pts.length ? `M${pts[0][0]},${pts[0][1]}` : "";
    let d = `M${pts[0][0]},${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
      const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
    }
    return d;
  };

  return (
    <div>
      <div className="flex gap-4 mb-4 flex-wrap">
        {keys.map(([k, label, c]) => <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{label}</span>)}
      </div>
      <div className="w-full">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 220 }}>
          <defs>
            {keys.map(([k, , c]) => (
              <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity="0.28" />
                <stop offset="100%" stopColor={c} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>
          {[0.25, 0.5, 0.75, 1].map((g) => <line key={g} x1="0" x2={W} y1={H - g * (H - 12) - 6} y2={H - g * (H - 12) - 6} stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" className="text-muted-foreground" />)}
          {keys.map(([k, , c]) => {
            const pts = series.map((d, i) => [xOf(i), yOf(d[k])]);
            const line = smooth(pts);
            const area = n > 1 ? `${line} L${xOf(n - 1)},${H} L${xOf(0)},${H} Z` : "";
            return (
              <g key={k}>
                {n > 1 && <path d={area} fill={`url(#g-${k})`} />}
                <path d={line} fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                {n === 1 && <circle cx={xOf(0)} cy={yOf(series[0][k])} r="4" fill={c} />}
                {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={c} vectorEffect="non-scaling-stroke" />)}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex mt-1 border-t border-border pt-1.5">
        {series.map((d, i) => (
          <div key={d.date} className="flex-1 text-center text-[10px] text-muted-foreground/80 tabular-nums">
            {i % step === 0 || i === n - 1 ? fmt(d.date) : ""}
          </div>
        ))}
      </div>
    </div>
  );
}
